// app/treatments/page.tsx
"use client";

import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import Link from "next/link";
import Navbar from "../../components/navbar";
import Footer from "../../components/footer";

const GREEN = "rgb(34 150 83)";

// Define the Treatment type
interface Treatment {
  id: string;
  name: string;
  description: string;
  image: string;
}

const TREATMENTS: Treatment[] = [
  {
    id: "dental-implants",
    name: "Dental Implants",
    description:
      "Replace missing teeth with titanium implants that look and feel natural. Lifetime warranty with 98% success rate.",
    image:
      "/treatments/implantss.png",
  },
  {
    id: "full-mouth-rehabilitation",
    name: "Full Mouth Rehabilitation (FMR)",
    description:
      "Comprehensive reconstruction of your entire oral structure. Restore function, aesthetics, and bite alignment.",
    image:
      "/treatments/fmr.png",
  },
  {
    id: "smile-designing",
    name: "Smile Designing",
    description:
      "Transform your smile with advanced cosmetic dentistry. Digital design and customized treatment plan.",
    image:
      "/treatments/smile.png",
  },
  {
    id: "veneers",
    name: "Porcelain Veneers",
    description:
      "Ultra-thin ceramic shells for a perfect smile. Cover chips, gaps, and stains permanently.",
    image:
      "/treatments/veeners.png",
  },
  {
    id: "braces-aligners",
    name: "Braces, Aligners & Invisalign",
    description:
      "Straighten teeth with traditional braces, clear aligners, or Invisalign. Custom, removable, and virtually invisible options.",
    image:
      "/treatments/clear.png",
  },
  {
    id: "root-canal-treatment",
    name: "Root Canal Treatment (RCT)",
    description:
      "Save infected teeth with Laser RCT, Microscopic RCT, and Single-sitting procedures. Painless, advanced microsurgery.",
    image:
      "/treatments/rct.webp",
  },
  {
    id: "tooth-extraction",
    name: "Tooth Extraction",
    description:
      "Safe and painless tooth extraction with modern techniques. Including wisdom tooth removal and surgical extractions.",
    image:
      "/treatments/extraction.png",
  },
  {
    id: "teeth-whitening",
    name: "Teeth Whitening",
    description:
      "Professional laser whitening for instant results. Get 8+ shades brighter in just one session.",
    image:
      "/treatments/whitening.webp",
  },
  {
    id: "crowns-bridges",
    name: "Crowns & Bridges",
    description:
      "Same-day CEREC crowns with precision fit. Durable ceramic crowns and bridges for long-lasting results.",
    image:
      "/treatments/crowns.png",
  },
  {
    id: "pediatric-dentistry",
    name: "Pediatric Dentistry",
    description:
      "Fun, fear-free visits for children. Cavity prevention, early intervention, and child-friendly specialists.",
    image:
      "/treatments/pediatric.png",
  },
];

export default function TreatmentsPage() {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { once: true, margin: "-10%" });
  // Removed unused expandedIndex state

  return (
    <>
      <Navbar />

      <div className="min-h-screen bg-gradient-to-b from-white via-emerald-50/30 to-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-white to-cyan-50 py-24 pt-32">
          <div className="relative z-10 mx-auto max-w-7xl px-6 text-center lg:px-12">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-black text-gray-900 lg:text-6xl"
            >
              Our Dental{" "}
              <span className="bg-gradient-to-r from-emerald-600 to-emerald-400 bg-clip-text text-transparent">
                Treatments
              </span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mx-auto mt-4 max-w-2xl text-xl text-gray-600"
            >
              Professional care for every smile. Click any card to learn more.
            </motion.p>
          </div>
        </section>

        {/* Treatments Grid */}
        <section ref={rootRef} className="py-16 pb-72">
          <div className="mx-auto max-w-7xl px-6 lg:px-12 space-y-10">
            {/* First 8 cards in 4-column grid */}
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {TREATMENTS.slice(0, 8).map((treatment, index) => (
                <Card
                  key={treatment.id}
                  treatment={treatment}
                  index={index}
                  inView={inView}
                />
              ))}
            </div>

            {/* Remaining cards (centered) */}
            <div className="flex flex-wrap justify-center gap-8">
              {TREATMENTS.slice(8).map((treatment, index) => (
                <Card
                  key={treatment.id}
                  treatment={treatment}
                  index={8 + index}
                  inView={inView}
                  customWidth="w-[270px] sm:w-[300px] lg:w-[320px]"
                />
              ))}
            </div>
          </div>
        </section>
      </div>

      <div className="pt-16">
        <Footer />
      </div>
    </>
  );
}

/* --- Reusable Card Component --- */
interface CardProps {
  treatment: Treatment;
  index: number;
  inView: boolean;
  customWidth?: string;
}

function Card({
  treatment,
  index,
  inView,
  customWidth = "w-full",
}: CardProps) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.05, duration: 0.4 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`${customWidth}`}
    >
      <Link href={`/treatments/${treatment.id}`}>
        <motion.div
          className="group relative flex h-[520px] flex-col overflow-hidden rounded-2xl border-2 border-emerald-200 bg-white shadow-md transition-all"
          whileHover={{
            y: -8,
            borderColor: GREEN,
            boxShadow: `0 20px 50px ${GREEN}20`,
          }}
          transition={{ duration: 0.3 }}
        >
          {/* Image */}
          <div className="relative h-1/2 overflow-hidden">
            <motion.img
              src={treatment.image}
              alt={treatment.name}
              className="h-full w-full object-cover"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.4 }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          </div>

          {/* Content */}
          <div className="flex flex-col justify-between p-5 h-1/2">
            <div>
              <h3 className="text-lg font-bold text-gray-900 line-clamp-2">
                {treatment.name}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-gray-600 line-clamp-4">
                {treatment.description}
              </p>
            </div>

            {/* CTA */}
            <motion.div
              className="mt-4 flex items-center justify-center gap-2 rounded-full bg-emerald-500 py-3 text-white"
              animate={isHovered ? { backgroundColor: GREEN } : {}}
            >
              <span className="font-semibold">View Details</span>
              <span>→</span>
            </motion.div>
          </div>
        </motion.div>
      </Link>
    </motion.div>
  );
}
